import { useState } from "react";
import OpacityWrapper from "../OpacityWrapper";
import { validateEmail, signUp } from "@helpers";
import ScaleOnHoverDiv from "../ScaleOnHoverDiv";
import clsx from "clsx";
import * as styles from "./styles";

const portalId = "22828643";
const formId = "8667f532-933e-4e7d-b7bb-6b17d8cf15a3"; // TODO: Replace with actual HubSpot form GUID

const carbonMarketRoles = [
    "What is your role in the carbon market?",
    "Buying carbon credits to retire",
    "Buying carbon credits to sell/trade",
    "Carbon credit project developer",
    "Investor in projects or funds",
    "Insurer of carbon credits",
    "Carbon credit standard/registry",
    "Research (no investment plans)",
];

const buttonStyle = clsx(
    "uppercase text-md tracking-widest font-bold py-3 text-center rounded-full max-w-[140px] bg-black text-white p-8 cursor-pointer"
);
const buttonDisabledStyle = clsx(
    "uppercase text-md tracking-widest font-bold py-3 text-center rounded-full max-w-[140px] text-mediumGray bg-black/10 p-8 cursor-not-allowed"
);

export default function RequestDemo() {
    const [formData, setFormData] = useState({
        firstname: "",
        lastname: "",
        company: "",
        email: "",
        job_title: "",
        what_is_your_role_in_the_carbon_market: "Please Select",
        newsletter_subscription: false,
    });

    const [enabled, setEnabled] = useState(false);
    const [displayMsg, setDisplayMsg] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === "checkbox" ? checked : value;

        const updatedFormData = {
            ...formData,
            [name]: newValue,
        };

        setFormData(updatedFormData);

        // Validate required fields
        const isEmailValid = validateEmail(updatedFormData.email);
        const hasRequiredFields =
            updatedFormData.firstname &&
            updatedFormData.lastname &&
            updatedFormData.company &&
            updatedFormData.email &&
            updatedFormData.what_is_your_role_in_the_carbon_market &&
            updatedFormData.what_is_your_role_in_the_carbon_market !== "Please Select";

        setEnabled(isEmailValid && hasRequiredFields);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!enabled) {
            return;
        }

        const hubspotUrl = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;

        // Prepare form data for HubSpot
        const submitData = {
            fields: Object.keys(formData)
                .filter((key) => {
                    // Only include fields with values
                    const value = formData[key];
                    if (key === "newsletter_subscription") {
                        return value; // Include checkboxes only if checked
                    }
                    return value && value !== "Please Select";
                })
                .map((key) => {
                    // Map internal field names to HubSpot property names
                    const hubspotFieldName = key;
                    return {
                        name: hubspotFieldName,
                        value:
                            typeof formData[key] === "boolean"
                                ? String(formData[key])
                                : formData[key],
                    };
                }),
            context: {
                pageUri: window.location.href,
                pageName: document.title,
            },
        };

        try {
            const response = await fetch(hubspotUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(submitData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            
            // If newsletter subscription is checked, subscribe to Mailchimp
            if (formData.newsletter_subscription && formData.email) {
                signUp(null, formData.email, (msg) => {
                    // Mailchimp subscription is handled silently
                    // The main success message will still show
                });
            }
            
            setDisplayMsg("Thank you for your interest! We'll be in touch soon.");
            setErrorMsg(null);

            // Reset form after success
            setFormData({
                firstname: "",
                lastname: "",
                company: "",
                email: "",
                job_title: "",
                what_is_your_role_in_the_carbon_market: "Please Select",
                newsletter_subscription: false,
            });
            setEnabled(false);

            // Clear success message after 7 seconds
            setTimeout(() => {
                setDisplayMsg(null);
            }, 7000);
        } catch (error) {
            console.error("Error submitting the form:", error);
            setErrorMsg("Failed to submit the form. Please try again.");
            setDisplayMsg(null);
        }
    };


    return (
        <OpacityWrapper className="relative mb-20 lg:mb-40">
            <div className={styles.bg}>
                <div className={styles.innerTextParentContainer}>
                    {/* REQUEST A DEMO TEXT */}
                    <div className="mr-8 max-w-[450px]">
                        <div className="text-[24px] uppercase tracking-wider">
                            Request a demo
                        </div>
                        <div className="tracking-1 mt-8 text-[18px] text-xl">
                            Reach out to us to discuss custom analysis for your
                            project or portfolio.
                        </div>
                    </div>
                    {/* FORM */}
                    <form
                        onSubmit={handleSubmit}
                        className="-mt-8 flex max-w-[550px] flex-col flex-1"
                    >
                        <div className={styles.formContainer}>
                            <div className={styles.successOrFailMsgContainer}>
                                {displayMsg && (
                                    <>
                                        <div className={styles.displayMsg}>
                                            {displayMsg}
                                        </div>
                                        <div
                                            className={styles.displayMsgPointer}
                                        ></div>
                                    </>
                                )}
                                {errorMsg && (
                                    <>
                                        <div className={styles.displayMsg}>
                                            {errorMsg}
                                        </div>
                                        <div
                                            className={styles.displayMsgPointer}
                                        ></div>
                                    </>
                                )}
                            </div>

                            {/* First Name and Last Name */}
                            <div className="flex gap-4 mb-4">
                                <div className="flex-1">
                                    <label className="text-black text-sm mb-2 block">
                                        First Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        required
                                        type="text"
                                        name="firstname"
                                        className={styles.input + " mb-0"}
                                        placeholder="First Name"
                                        value={formData.firstname}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="text-black text-sm mb-2 block">
                                        Last Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        required
                                        type="text"
                                        name="lastname"
                                        className={styles.input + " mb-0"}
                                        placeholder="Last Name"
                                        value={formData.lastname}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            {/* Company */}
                            <div className={styles.inputContainer}>
                                <label className="text-black text-sm mb-2 block">
                                    Company <span className="text-red-500">*</span>
                                </label>
                                <input
                                    required
                                    type="text"
                                    name="company"
                                    className={styles.input}
                                    placeholder="Company"
                                    value={formData.company}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* Email */}
                            <div className={styles.inputContainer}>
                                <label className="text-black text-sm mb-2 block">
                                    Email <span className="text-red-500">*</span>
                                </label>
                                <input
                                    required
                                    type="email"
                                    name="email"
                                    className={styles.input}
                                    placeholder="Email"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* Job Title */}
                            <div className={styles.inputContainer}>
                                <input
                                    type="text"
                                    name="job_title"
                                    className={styles.input}
                                    placeholder="Job Title (optional)"
                                    value={formData.job_title}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* Carbon Market Role Dropdown */}
                            <div className={styles.inputContainer}>
                                <label className="text-black text-sm mb-2 block">
                                    Your role in the carbon markets is...{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <select
                                    required
                                    name="what_is_your_role_in_the_carbon_market"
                                    className={styles.select}
                                    value={formData.what_is_your_role_in_the_carbon_market}
                                    onChange={handleChange}
                                >
                                    {carbonMarketRoles.map((role) => (
                                        <option key={role} value={role}>
                                            {role}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Newsletter Checkbox */}
                            <div className={styles.subscribeContainer}>
                                <input
                                    type="checkbox"
                                    name="newsletter_subscription"
                                    checked={formData.newsletter_subscription}
                                    onChange={handleChange}
                                    className="mr-2"
                                />
                                <div className="-mt-[5px]">
                                Subscribe to our newsletter
                                <br /> By checking this box, you agree to receive communications from CTrees. You can opt-out at any time.
                                </div>
                            </div>

                            <div className="mx-auto">
                                <ScaleOnHoverDiv>
                                    <button
                                        type="submit"
                                        disabled={!enabled}
                                        className={
                                            enabled ? buttonStyle : buttonDisabledStyle
                                        }
                                    >
                                        Submit
                                    </button>
                                </ScaleOnHoverDiv>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </OpacityWrapper>
    );
}

